import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Button, Icon } from '@factorialco/factorial-one-react'
import { ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Maximize, Minimize, Pencil } from '@factorialco/factorial-one-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import NodeModal from 'modules/job_catalog/components/NodeModal'
import { useNodeModalContent } from 'modules/job_catalog/hooks/useModalContent'
import useUpdateJobCatalogTreeNode from 'modules/job_catalog/hooks/useUpdateJobCatalogTreeNode'
import {
  NODE_TYPES,
  NodeFormValues,
  SalaryRangeValue,
  TreeNode,
  countNodeTypes,
  findNodeByUuid,
  formatSalaryRange,
  formatUuidToNodeType,
  getParentChain,
  getTextFromNodeType,
  isNodeType,
  maximizeClickPath,
  nodeTypeIcon,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

import SalaryModal from '../SalaryModal'

type Props = {
  name: string
  description: string
  type: string
  canEditRole: boolean
  childrens?: TreeNode[]
  salaryRangeValue?: SalaryRangeValue
  currentNode?: TreeNode
  isMaximized?: boolean
}

export const NodeHeader = ({
  name,
  description,
  type,
  childrens = [],
  salaryRangeValue,
  canEditRole,
  currentNode,
  isMaximized = false,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const { id } = useParams<{ id: string }>()
  const normalizedType = normalizeNodeType(type)
  const [showEditNodeModal, setShowEditNodeModal] = useState(false)
  const [showEditSalaryModal, setShowSalaryNodeModal] = useState(false)
  const modalContent = useNodeModalContent('edit', name, normalizedType)
  const typeLabel = isNodeType(type) ? getTextFromNodeType(normalizedType) : 'Unknown'
  const childrenTypeCounts = countNodeTypes(childrens)
  const { updateNode } = useUpdateJobCatalogTreeNode()
  const formattedSalary = salaryRangeValue ? formatSalaryRange(salaryRangeValue) : ''

  const nodeToUse = useMemo(() => {
    if (currentNode) return currentNode
    if (!id || !childrens.length) return undefined
    return findNodeByUuid(childrens, id)
  }, [currentNode, childrens, id])

  const metadata = [
    ...Object.entries(childrenTypeCounts)
      .filter(([childType]) => normalizeNodeType(childType) !== NODE_TYPES.LEVEL)
      .map(([childType, count]) => ({
        label: isNodeType(childType) ? getTextFromNodeType(childType) : 'Unknown',
        value: {
          type: 'text' as const,
          content: (
            <Box flexDirection="row" justifyContent="center" gap="s4">
              <Icon icon={nodeTypeIcon(isNodeType(childType) ? childType : NODE_TYPES.ROOT)} />
              {count}
            </Box>
          ),
        },
      })),
    {
      label: i18n.t('job_catalog.nodes.salary'),
      value: {
        type: 'text' as const,
        content: formattedSalary || i18n.t('job_catalog.nodes.no_salary'),
      },
      actions: [
        {
          icon: Pencil,
          label: i18n.t('common.edit'),
          onClick: () => {
            setShowSalaryNodeModal(true)
          },
        },
      ],
    },
  ].filter(Boolean)

  const handleMaximizeClick = () => {
    if (!nodeToUse || !id) return

    navigate(
      maximizeClickPath(
        getParentChain(nodeToUse),
        normalizeNodeType(nodeToUse.type),
        id,
        navigation,
        nodeToUse.type
      )
    )
  }

  const handleMinimizeClick = () => {
    const nodeId = nodeToUse?.node_uuid || ''
    navigate(
      navigation.employees.jobCatalog.overview.node.fullPath({
        id: nodeId,
      })
    )
  }

  const handleButtonClick = () => {
    if (isMaximized) {
      handleMaximizeClick()
    } else {
      handleMinimizeClick()
    }
  }

  const handleEditNode = async ({ name, description }: NodeFormValues) => {
    const result = await updateNode(
      company.id,
      description,
      name,
      nodeToUse?.id || 0,
      formatUuidToNodeType(type),
      Number(nodeToUse?.ancestor_id)
    )
    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.modal.edit_success', { type: type }),
        time: 5000,
      })
    }
    setShowEditNodeModal(false)
  }
  const initialValues: NodeFormValues = {
    name: nodeToUse?.name || '',
    description: nodeToUse?.description || '',
    children: [],
  }

  return (
    <Box flexDirection="row" justifyContent="spaceBetween">
      <Box width="full">
        <ResourceHeader
          title={name}
          description={description}
          // @ts-expect-error Element vs string type mismatch in metadata
          metadata={metadata}
          secondaryActions={[
            {
              label: i18n.t('common.edit'),
              icon: Pencil,
              isVisible: canEditRole,
              onClick: () => setShowEditNodeModal(true),
            },
          ]}
          status={{
            label: '',
            text: typeLabel,
            variant: 'neutral',
          }}
        />
      </Box>
      <Box paddingRight="s20" paddingTop="s12">
        <Button
          hideLabel
          icon={isMaximized ? Maximize : Minimize}
          label="Outline"
          onClick={handleButtonClick}
          round
          size="md"
          variant="outline"
        />
      </Box>
      {showEditNodeModal && (
        <NodeModal
          onClose={() => setShowEditNodeModal(false)}
          title={modalContent.title}
          description={modalContent.description}
          onSubmit={handleEditNode}
          icon={modalContent.icon || 'pencil'}
          nodeType={normalizedType}
          initialValues={initialValues}
          actionType="edit"
        />
      )}
      {showEditSalaryModal && (
        <SalaryModal
          onClose={() => setShowSalaryNodeModal(false)}
          name={name}
          onSubmit={() => setShowSalaryNodeModal(false)}
          nodeType={normalizedType}
          initialSalary={{
            min: salaryRangeValue?.min,
            max: salaryRangeValue?.max,
            gross: salaryRangeValue?.gross,
          }}
        />
      )}
    </Box>
  )
}
